import { forwardRef } from 'react';
import { Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * BrandCTA — landing-page CTA that mirrors the in-app "Start building today!"
 * button styling (cream pill, amber text, sparkles icon).
 *
 * Variants:
 *  - primary (default): cream/amber filled pill
 *  - outline: transparent pill with matching border
 * Sizes: sm | md | lg
 */
type Variant = 'primary' | 'outline';
type Size = 'sm' | 'md' | 'lg';

const base =
  'inline-flex items-center justify-center gap-1.5 font-semibold rounded-full transition-all whitespace-nowrap touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none';

const variants: Record<Variant, string> = {
  primary:
    'bg-brand-cta text-brand-cta-foreground border border-brand-cta-border hover:bg-brand-cta-hover shadow-sm',
  outline:
    'bg-transparent text-brand-cta-outline-fg border border-brand-cta-outline-border hover:bg-brand-cta-outline-hover',
};

const sizes: Record<Size, string> = {
  sm: 'text-sm px-3 py-1.5 h-11 sm:h-9 [&_svg]:size-3.5',
  md: 'text-sm px-5 h-11 sm:h-10 [&_svg]:size-4',
  lg: 'text-base px-7 h-12 [&_svg]:size-4',
};

export interface BrandCTAProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  asChild?: boolean;
  withIcon?: boolean;
}

export const brandCtaClass = (variant: Variant = 'primary', size: Size = 'md', className?: string) =>
  cn(base, variants[variant], sizes[size], className);

export const BrandCTA = forwardRef<HTMLButtonElement, BrandCTAProps>(
  ({ variant = 'primary', size = 'md', withIcon = true, className, children, ...props }, ref) => (
    <button ref={ref} className={brandCtaClass(variant, size, className)} {...props}>
      {withIcon && <Sparkles aria-hidden="true" />}
      {children}
    </button>
  ),
);
BrandCTA.displayName = 'BrandCTA';